<template>
  <vl-region class="region">
    <vl-layout>
      <template v-if="detail && !loading">
        <vl-link-list mod-inline>
          <vl-link-list-item>
            <vl-link :to="`/publication/${id}`" mod-block mod-bold mod-no-visited-state :exact="true">
              {{ getVersionLabel() }}
            </vl-link>
          </vl-link-list-item>
        </vl-link-list>
        <vl-title>{{
          isDatasetserie ? $t('publications.versions.detail.dataset') : $t('publications.versions.detail.version')
        }}</vl-title>
        <vl-grid v-vl-spacer:bottom.small>
          <vl-column>
            <vl-properties mod-full-width>
              <vl-properties-column>
                <vl-properties-list class="properties">
                  <vl-properties-label>{{ $t('publications.versions.detail.startedDate') }}</vl-properties-label>
                  <vl-properties-data>{{
                    detail.creationDate ? dateTimeFilter(detail.creationDate) : `-`
                  }}</vl-properties-data>
                  <vl-properties-label>{{ $t('publications.versions.detail.status') }}</vl-properties-label>
                  <vl-properties-data>{{ getVersionStatus() }}</vl-properties-data>
                  <vl-properties-label>{{ $t('publications.versions.detail.publicationDate') }}</vl-properties-label>
                  <vl-properties-data>{{
                    detail.productEndExecution && isDeliverySuccessful
                      ? dateTimeFilter(detail.productEndExecution)
                      : `-`
                  }}</vl-properties-data>
                </vl-properties-list>
              </vl-properties-column>
            </vl-properties>
          </vl-column>
        </vl-grid>
        <vl-grid v-if="canDownloadDeliveryFile" v-vl-spacer:bottom.small>
          <vl-column>
            <vl-properties mod-full-width>
              <vl-properties-column>
                <vl-properties-list class="properties">
                  <vl-properties-label>{{ $t('publications.versions.detail.file') }}</vl-properties-label>
                  <vl-properties-data>
                    <div>
                      <vl-button
                        type="button"
                        mod-icon
                        mod-link
                        icon="file"
                        :mod-loading="data.loadingDownload"
                        :mod-disabled="data.loadingDownload"
                        @click="downloadFile"
                        >{{ detail.deliveryFileName }}
                      </vl-button>
                      <vl-loader
                        v-if="data.loadingDownload"
                        :message="$t('general.downloading')"
                        class="vl-u-display-flex"
                      />
                    </div>
                  </vl-properties-data>
                </vl-properties-list>
              </vl-properties-column>
            </vl-properties>
          </vl-column>
        </vl-grid>
        <vl-grid v-vl-spacer:bottom.small>
          <vl-column>
            <vl-properties mod-full-width>
              <vl-properties-list class="properties">
                <vl-properties-label>{{ $t('publications.versions.detail.versionDate') }}</vl-properties-label>
                <vl-properties-data>{{ detail.versionDate ? dateFilter(detail.versionDate) : `-` }}</vl-properties-data>
                <vl-properties-label>{{ $t('publications.versions.detail.temporalDate') }}</vl-properties-label>
                <vl-properties-data>{{ getTemporalDate() }}</vl-properties-data>
                <vl-properties-label>{{ $t('publications.versions.detail.versionDescription') }}</vl-properties-label>
                <vl-properties-data>{{
                  detail.versionDescription ? detail.versionDescription : `-`
                }}</vl-properties-data>
              </vl-properties-list>
            </vl-properties>
          </vl-column>
        </vl-grid>
        <vl-grid v-vl-spacer:bottom.medium class="links-grid">
          <vl-column v-if="metadataUrl">
            <vl-link
              :href="metadataUrl"
              mod-icon-before
              icon="external"
              mod-no-visited-state
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ $t('publications.versions.detail.metadata') }}
            </vl-link>
          </vl-column>
          <vl-column v-if="qualityReportUrl" v-vl-spacer:top.small>
            <vl-link mod-icon-before icon="external" :href="qualityReportUrl" target="_blank">{{
              $t('publications.versions.detail.qualityreport')
            }}</vl-link>
          </vl-column>
          <vl-column v-if="deliverySpecificationsUrl" v-vl-spacer:top.small>
            <vl-link mod-icon-before icon="file-office-pdf" :href="deliverySpecificationsUrl" target="_blank">{{
              $t('publications.versions.detail.deliverySpecifications')
            }}</vl-link>
          </vl-column>
        </vl-grid>
        <vl-grid>
          <vl-column>
            <ProductChangeHistoryComponent :run-id="detail?.productRunGuid" />
          </vl-column>
        </vl-grid>
      </template>
      <template v-if="loading">
        <vl-region>
          <div v-vl-align:center>
            <vl-loader :message="$t('publications.versions.detail.loading')" />
          </div>
        </vl-region>
      </template>
    </vl-layout>
  </vl-region>
</template>
<script lang="ts" setup>
import { onMounted, onUnmounted, computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePublicationVersionDetailStore } from '../store/publication-version-detail.store';
import { useRoute } from 'vue-router';
import ProductChangeHistoryComponent from '@/modules/products/views/subcomponents/ProductChangeHistoryComponent.vue';
import { dateFilter, dateTimeFilter } from '@/filters';
import { PublicationVersion } from '@/api/frontoffice/clients';
import { PublicationVersionState } from '../common';
import { config } from '@/common/config/config';
import { useAlertStore } from '@/modules/core/store/alert.store';

const { t } = useI18n();
const detailStore = usePublicationVersionDetailStore();
const alertStore = useAlertStore();

const route = useRoute();

const data = reactive({
  loadingDownload: false
});

const isDatasetserie = computed((): boolean => {
  return !detail.value?.isProductContinuous;
});

const canDownloadDeliveryFile = computed((): boolean => {
  return false;
  // TODO
  // return !!detail.value?.deliveryFileName;
});

const id = computed((): string => {
  return route.params.id as string;
});

const downloadFile = async () => {
  data.loadingDownload = true;
  try {
    await detailStore.downloadDeliveryFile(id.value, versionId.value);
  } catch (error) {
    alertStore.handleException(t('publications.versions.detail.error.download.title'), error);
  } finally {
    data.loadingDownload = false;
  }
};

const metadataUrl = computed((): string | null => {
  const uuid = isDatasetserie.value ? detail.value?.versionMetadataUUID : detail.value?.productMetadataUUID;
  return uuid ? `${config.integrations.metadataUrl}/${uuid}`.toString() : null;
});

const deliverySpecificationsUrl = computed((): string | null => {
  return ''; // TODO
});

const qualityReportUrl = computed((): string | null => {
  return detail.value.deliveryQualityReport ? detail.value.deliveryQualityReport : null;
});

const getVersionLabel = (): string => {
  const label = detail.value.productLabel;
  return label ? label : t('publications.versions.detail.publication');
};

const versionState = computed((): PublicationVersionState => {
  return new PublicationVersionState(detail.value);
});

const getVersionStatus = (): string => {
  return versionState.value.description ? versionState.value.description : `-`;
};

const isDeliverySuccessful = computed((): boolean => {
  return !versionState.value?.isInError;
});

const getTemporalDate = (): string => {
  const from = detail.value.versionValidFrom ? dateFilter(detail.value.versionValidFrom) : '';
  const to = detail.value.versionValidUntil ? dateFilter(detail.value.versionValidUntil) : '';

  if (from && to) {
    return t('publications.versions.detail.fromAndToDateDescription', { from, to });
  } else if (from) {
    return t('publications.versions.detail.fromDateDescription', { from });
  }
  return `-`;
};

const versionId = computed((): string => {
  return route.params.versionId as string;
});

const loading = computed((): boolean => {
  return detailStore.loading;
});

const detail = computed((): PublicationVersion => {
  return detailStore.publicationVersion;
});

onMounted(async () => {
  await detailStore.getPublicationVersion(id.value, versionId.value);
});

onUnmounted(() => {
  detailStore.clear();
});
</script>

<style lang="scss" scoped>
.properties .vl-properties__label,
.properties .vl-properties__data {
  padding-bottom: 5px;
}

.properties .vl-properties__label {
  flex: 0 0 30%;
}

.properties .vl-properties__data {
  flex: 0 0 70%;
}

.links-grid .vl-link {
  margin-bottom: 10px;
}
</style>
